/* eslint-disable react/jsx-props-no-spreading */
import type { FC } from 'react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// mantine
import { Button, ButtonProps } from '@mantine/core';
import { useTimeout } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
// queries
import type { IEventConnectorData } from '@newo/api/gateway';
import { usePublishProjectMutation } from '@/services/designer/projects/projects.queries.ts';
import { useProjectsBatchPublishMutation } from '@/modules/Projects/Projects.queries.ts';
import { useSendConnectorEventMutation } from '@/services/events/events.queries.tsx';
import { QUERY_KEYS } from '@/services/bff/bff.queries.ts';

type PublishProjectButtonProject = {
    id: string;
    idn: string;
    version?: string | null;
};

interface PublishProjectButtonProps extends ButtonProps {
    project?: PublishProjectButtonProject;
}

const SYNC_TIMEOUT = 10000;

const PublishProjectButton: FC<PublishProjectButtonProps> = ({
    project,
    children,
    ...props
}) => {
    const queryClient = useQueryClient();
    const [isSyncDisabled, setIsSyncDisabled] = useState(false);
    const { mutateAsync: publishProject, isPending: isPublishPending } =
        usePublishProjectMutation();
    const {
        mutateAsync: batchPublishProjects,
        isPending: isBatchPublishPending
    } = useProjectsBatchPublishMutation();
    const { mutate: sendConnectorEvent, isPending } =
        useSendConnectorEventMutation();

    const { start } = useTimeout(() => {
        setIsSyncDisabled(false);
    }, SYNC_TIMEOUT);

    const handlePublish = async () => {
        await (project
            ? publishProject({
                  projectId: project.id,
                  data: {
                      version: project.version ?? '',
                      description: project.idn,
                      type: 'public'
                  }
              })
            : batchPublishProjects());
        queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.getAgentsList()
        });
        sendConnectorEvent(
            {
                integration_idn: 'api',
                connector_idn: 'environment',
                event_idn: 'start_update_event'
            } as IEventConnectorData,
            {
                onSuccess: () => {
                    notifications.show({
                        color: 'green',
                        message: 'Agent published successfully'
                    });
                }
            }
        );
        setIsSyncDisabled(true);
        start();
    };

    return (
        <Button
            {...props}
            disabled={isSyncDisabled}
            loaderProps={{ variant: 'dots' }}
            loading={isPending || isPublishPending || isBatchPublishPending}
            size="xs"
            onClick={handlePublish}
        >
            {children}
        </Button>
    );
};

export default PublishProjectButton;
